import { findCust, findFundAbbById, findStrategy } from "../component/Methods";
import {
  findLivePriceForTicker,
  formatTickerDisplay,
  getLivePriceTickerKey,
  normalizeLivePriceEntry,
} from "./livePriceTickerMatch";

function formatTimeAgo(dateString) {
  if (!isValidDate(dateString)) {
    return { valid: false, msg: "N/A" };
  }
  const currentDate = new Date();
  const givenDate = new Date(parseInt(dateString, 10) * 1000);

  const timeDifference = currentDate - givenDate;
  const threshold = 20;
  const minutesDifference = Math.floor(timeDifference / (1000 * 60));

  let toreturn = { valid: true };
  if (minutesDifference > parseFloat(threshold)) {
    toreturn.valid = false;
  }

  if (minutesDifference < 1) {
    toreturn.msg = "just now";
    return toreturn;
  } else if (minutesDifference === 1) {
    toreturn.msg = "1 min ago";
    return toreturn;
  } else if (minutesDifference < 60) {
    toreturn.msg = `${minutesDifference} min ago`;
    return toreturn;
  } else {
    const hoursDifference = Math.floor(minutesDifference / 60);

    if (hoursDifference === 1) {
      toreturn.msg = "1 hour ago";
      return toreturn;
    } else if (hoursDifference < 24) {
      toreturn.msg = `${hoursDifference} hours ago`;
      return toreturn;
    } else {
      const daysDifference = Math.floor(hoursDifference / 24);

      if (daysDifference === 1) {
        toreturn.msg = "1 day ago";
        return toreturn;
      } else {
        toreturn.msg = `${daysDifference} days ago`;
        return toreturn;
      }
    }
  }
}
function formatTimeAgo5minutes(dateString) {
  if (!isValidDate(dateString)) {
    // return { valid: false, msg: "N/A" };
    return { valid: true, msg: "true" };
  }

  const currentDate = new Date();
  const givenDate = new Date(parseInt(dateString, 10) * 1000);
  const timeDifference = currentDate - givenDate;
  const minutesDifference = Math.floor(timeDifference / (1000 * 60));

  if (minutesDifference <= 5) {
    return { valid: true, msg: "true" };
  } else {
    // return { valid: true, msg: "false" };
    return { valid: true, msg: "true" };
  }
}

function isValidDate(dateString) {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

function capitalizeFirstLetters(str) {
  return str.replace(/\b\w/g, function (char) {
    return char.toUpperCase();
  });
}

function getSecuritiesWithMarketDataRequest(
  lots,
  selectedFundsId = [],
  strategyIds = [],
  strategies = [],
  livePrices
) {
  if (!Array.isArray(selectedFundsId)) {
    console.error("fundIds must be an array.");
    return { securities: [], market_data_request: [] }; // Or throw an error
  }

  const initialFilteredLots = lots.filter((lot) => {
    const strategyMatches =
      strategyIds.length === 0 || strategyIds?.includes(lot.strategy);
    const validShareCount = lot?.shareCount != 0;
    return strategyMatches && validShareCount;
  });

  let filteredLots = [];
  selectedFundsId?.forEach((fundId) => {
    const fundFilteredLots = initialFilteredLots.filter(
      (lot) => lot.fund === fundId
    );
    filteredLots = filteredLots.concat(fundFilteredLots);
  });

  const signalSets = strategies
    .filter((strategy) => strategyIds?.includes(strategy?.strategyId))
    .map((strategy) => strategy?.signals);

  const tickerMap = {};

  filteredLots?.forEach((lot) => {
    const { ticker } = lot;
    //TODO: This is for Bloomberg error
    const symbol = ticker.stock
      ? `${ticker.symbol}/${ticker.stock}`
      : ticker.symbol;
    // const symbol = ticker.symbol;
    const investmentType =
      ticker?.investment === 4
        ? "equity"
        : ticker?.investment === 10
        ? "options"
        : null;

    if (!tickerMap[symbol]) {
      tickerMap[symbol] = {
        ticker: ticker,
        type: investmentType,
      };
    } else {
      if (tickerMap[symbol].type !== investmentType && investmentType) {
        tickerMap[symbol].type = "equity & options";
      }
    }
  });

  signalSets.forEach((signalSet) => {
    const signalTickers = signalSet?.weights_keys;

    for (const key in signalTickers) {
      let { symbol, investment, stock } = signalTickers[key];
      //TODO: This is for Bloomberg error
      symbol = stock ? `${symbol}/${stock}` : symbol;
      const investmentType =
        investment === 4 ? "equity" : investment === 9 ? "options" : null;

      if (!tickerMap[symbol]) {
        tickerMap[symbol] = {
          ticker: signalTickers[key],
          type: investmentType,
        };
      } else {
        if (tickerMap[symbol].type !== investmentType && investmentType) {
          tickerMap[symbol].type = "equity & options";
        }
      }
    }
  });

  const securities = Object.values(tickerMap);

  const groupedRequests = {
    equity: {
      market: "fmp",
      threshold: 1200,
      request: true,
      fields: [9, 10],
      tickers: [],
    },
    options: {
      market: "fmp",
      threshold: 1200,
      request: true,
      fields: [11],
      tickers: [],
    },
    "equity & options": {
      market: "fmp",
      threshold: 1200,
      request: true,
      fields: [9, 10, 11],
      tickers: [],
    },
    //TEMP: DO NOT PUSH TO PROD
    equity_alt: {
      market: "OpenFigi",
      request: true,
      fields: [14],
      tickers: [],
    },
    options_alt: {
      market: "OpenFigi",
      request: true,
      fields: [14],
      tickers: [],
    },
    "equity & options_alt": {
      market: "OpenFigi",
      request: true,
      fields: [14],
      tickers: [],
    },
    equity_alt2: {
      market: "factset",
      request: true,
      fields: [1, 11, 13],
      tickers: [],
    },
    options_alt2: {
      market: "factset",
      request: true,
      fields: [1, 11, 13],
      tickers: [],
    },
    "equity & options_alt2": {
      market: "factset",
      request: true,
      fields: [1, 11, 13],
      tickers: [],
    },
    //END TEMP
  };

  securities.forEach((security) => {
    const livePrice = findLivePriceForTicker(livePrices, security.ticker);

    const expirationDate = livePrice
      ? new Date(livePrice.expiration * 1000)
      : null;
    const currentTime = new Date(Date.now());

    // const currentTimeWithMargin = new Date(
    //   currentTime.getTime() - 20 * 24 * 60 * 60 * 1000
    // );

    const currentTimeWithMargin = new Date(
      currentTime.getTime() - 20 * 60 * 1000
    );

    // const currentTimeWithMargin = new Date(currentTime.getTime() - 60 * 60 * 1000);

    const isExpired = expirationDate
      ? expirationDate < currentTimeWithMargin
      : true;

    if (!livePrice || isExpired) {
      if (groupedRequests[security.type]) {
        //TODO: This is for Bloomberg error
        // const { stock, ...tickerWithoutStock } = security.ticker;
        // const symbol = stock
        //   ? `${security.ticker.symbol}/${stock}`
        //   : security.ticker.symbol;
        // groupedRequests[security.type].tickers.push({
        //   ...tickerWithoutStock,
        //   symbol,
        // });

        groupedRequests[security.type].tickers.push(security.ticker);
        if (
          security.type === "equity" ||
          security.type === "equity & options"
        ) {
          groupedRequests["equity_alt"].tickers.push(security.ticker);
          groupedRequests["equity_alt2"].tickers.push(security.ticker);
        }
      }
    }
  });

  const market_data_request = Object.values(groupedRequests).filter(
    (request) => request.tickers.length > 0
  );

  return {
    securities: securities,
    market_data_request: market_data_request,
  };
}

function getStrategiesContainingTicker(strategies, tickerSymbol) {
  const foundInStrategies = [];

  strategies?.forEach((strategy) => {
    const signalSet = strategy?.signals?.weights_keys;

    if (signalSet && typeof signalSet === "object") {
      const tickerExists = Object.values(signalSet).some(
        (signalTicker) =>
          signalTicker?.symbol?.toLowerCase() === tickerSymbol?.toLowerCase()
      );

      if (tickerExists) {
        foundInStrategies.push(strategy.name);
      }
    }
  });

  return foundInStrategies;
}

function getPositions(
  funds = [],
  strategies,
  lots,
  custodians,
  fundId = null,
  strategyId = null
) {
  const filteredLots = lots?.filter((lot) => {
    const fundMatch = fundId != null ? lot.fund === fundId : true;
    const strategyMatch = strategyId ? lot.strategy === strategyId : true;
    const custodianName = findCust(lot?.account?.custodian, custodians);
    const custodianMatch = custodianName
      ? custodianName?.toLowerCase() !== "held away"
      : true;
    return fundMatch && strategyMatch && custodianMatch;
  });

  const positionMap = new Map();

  filteredLots.forEach((lot) => {
    const costPerShare = lot.shareCount !== 0 ? lot.cost / lot.shareCount : 0;
    const lotWithCostPerShare = {
      ...lot,
      costPerShare: Number(costPerShare.toFixed(2)),
    };

    const tickerKey = getLivePriceTickerKey(normalizeLivePriceEntry(lot.ticker));
    const key = `${lot.fund}_${lot.account?.custodian || "no-custodian"}_${tickerKey}_${
      lot.strategy || "no-strategy"
    }`;

    if (!positionMap.has(key)) {
      positionMap.set(key, {
        ticker: lot.ticker,
        tickerSymbol: formatTickerDisplay(lot.ticker),
        fundName: lot.fund ? findFundAbbById(lot.fund, funds) : "-",
        custodianName: lot.account?.custodian
          ? findCust(lot.account?.custodian, custodians)
          : "-",
        strategyName: lot.strategy
          ? findStrategy(lot.strategy, strategies)
          : "-",
        fund: lot.fund,
        custodian: lot.account?.custodian || "no-custodian",
        strategy: lot.strategy || null,
        shares: 0,
        lots: [],
        positionType: null, // Will be determined after all lots are added
        averageCostPerShare: 0, // Will be calculated after all lots are added
        price: 0,
        investment: lot.ticker?.investment,
      });
    }

    const position = positionMap.get(key);
    position.shares += lot.shareCount;
    position.lots.push(lotWithCostPerShare);
  });

  positionMap.forEach((position) => {
    const hasLong = position.lots.some((lot) => lot.position === 0);
    const hasShort = position.lots.some((lot) => lot.position === 1);

    if (hasLong && hasShort) {
      position.positionType = "Boxed";
    } else if (hasShort) {
      position.positionType = "Short";
    } else if (hasLong) {
      position.positionType = "Long";
    } else {
      position.positionType = "Unknown";
    }

    const totalCost = position.lots.reduce((sum, lot) => sum + lot.cost, 0);
    position.averageCostPerShare =
      position.shares !== 0
        ? Number((totalCost / position.shares).toFixed(2))
        : 0;
    position.price = position.lots[0].sharePrice;
  });

  return Array.from(positionMap.values());
}

function isEquityLot(lot) {
  return Number(lot?.ticker?.investment) === 4;
}

function getBoxedPositions(lots, custodians) {
  const filteredLots = lots.filter(
    (lot) =>
      isEquityLot(lot) &&
      lot.account?.custodian !== "b409d30c6d7145a91be1ece0413f3c33" &&
      findCust(lot?.account?.custodian, custodians)?.toLowerCase() !==
        "held away"
  );

  const groupedLots = new Map();

  filteredLots.forEach((lot) => {
    const tickerKey = getLivePriceTickerKey(normalizeLivePriceEntry(lot.ticker));
    const key = `${lot.fund}_${tickerKey}_${lot.account?.custodian || "-"}`;

    if (!groupedLots.has(key)) {
      groupedLots.set(key, {
        fund: lot.fund,
        tickerSymbol: formatTickerDisplay(lot.ticker),
        ticker: lot.ticker,
        investment: lot.ticker?.investment,
        custodian: lot.account?.custodian || "no-custodian",
        lots: [],
        totalShares: 0,
        hasPositiveShares: false,
        hasNegativeShares: false,
      });
    }

    const group = groupedLots.get(key);
    group.lots.push(lot);
    group.totalShares += lot.shareCount;

    if (lot.shareCount > 0) {
      group.hasPositiveShares = true;
    } else if (lot.shareCount < 0) {
      group.hasNegativeShares = true;
    }
  });

  const boxedPositions = Array.from(groupedLots.values())
    .filter((group) => group.hasPositiveShares && group.hasNegativeShares)
    .map((group) => ({
      fund: group.fund,
      tickerSymbol: group.tickerSymbol,
      ticker: group.ticker,
      investment: group.investment,
      custodian: group.custodian,
      totalShares: group.totalShares,
      lots: group.lots,
    }));

  return boxedPositions;
}

function generateEodFileName(report, timestamp) {
  if (report?.toLowerCase() == "goldman") {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });

    const nowParts = {};
    formatter.formatToParts(new Date(timestamp)).forEach((part) => {
      if (
        part.type === "year" ||
        part.type === "month" ||
        part.type === "day" ||
        part.type === "hour" ||
        part.type === "minute" ||
        part.type === "second"
      ) {
        nowParts[part.type] = parseInt(part.value);
      }
    });

    return `tradefile.nine82.${nowParts["year"]?.toString()}${nowParts["month"]
      ?.toString()
      .padStart(2, "0")}${nowParts["day"]
      ?.toString()
      .padStart(2, "0")}${nowParts["hour"]
      ?.toString()
      .padStart(2, "0")}${nowParts["minute"]
      ?.toString()
      .padStart(2, "0")}${nowParts["second"]?.toString().padStart(2, "0")}.csv`;
  } else if (report?.toLowerCase() == "topwater") {
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);

    return `TopWater_${day}-${month}-${year}.csv`;
  } else if (report?.toLowerCase() == "fundcount") {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `PCMBlotter ${year}-${month}-${day}.csv`;
  } else return "eod_report.csv";
}

export {
  formatTimeAgo,
  formatTimeAgo5minutes,
  capitalizeFirstLetters,
  getSecuritiesWithMarketDataRequest,
  getStrategiesContainingTicker,
  getPositions,
  getBoxedPositions,
  generateEodFileName,
};
